import { BaseApplication, NormalRender, Graphics, ResourceContainer, ViewManager, CameraManager, SceneManager } from './engine';

import DatGui from './components/DatGui';
import SceneController from './components/SceneController';
import HomeView from './views/HomeView';

import { sRGBEncoding } from 'three';
import { Scene } from 'three';
import { CubeRefractionMapping } from 'three';
import { RepeatWrapping } from 'three';
import { PCFSoftShadowMap } from 'three';
import { PCFShadowMap } from 'three';
import { VSMShadowMap } from 'three';
import { MirroredRepeatWrapping } from 'three';
import { Color } from 'three';
import { ACESFilmicToneMapping } from 'three';
import { PlaneGeometry } from 'three';
import { ShadowMaterial } from 'three';
import { Mesh } from 'three';

import { Sections } from './views/Sections';

//
import Structure from './components/Structure';
import Sun from './components/Sun';
import Sky from './components/Sky';

import CustomNormalRender from './CustomNormalRender';

export default class MainApplication extends BaseApplication
{
  init()
  {
    this.scene_controller = SceneController;
    this.normal_render_mode = new NormalRender();

    this.scene_controller.init();

    Graphics.set_state(new CustomNormalRender());
    //Graphics.set_state(this.normal_render_mode);
    Graphics._renderer.outputEncoding = sRGBEncoding;

    Graphics._renderer.toneMapping = ACESFilmicToneMapping;
    Graphics._renderer.shadowMap.type = VSMShadowMap;
    //Graphics._renderer.shadowMap.type = PCFSoftShadowMap;

    Graphics._renderer.shadowMap.enabled = true;

    Graphics._renderer.setClearColor(undefined, 0);
    Graphics._renderer.autoClear = false;
    DatGui.init();

  }

  on_enter()
  {
    ResourceContainer.get_resource('cube').mapping = CubeRefractionMapping;
    ResourceContainer.get_resource('cube').encoding = sRGBEncoding;

    ResourceContainer.get_resource('cube-blur').mapping = CubeRefractionMapping;
    ResourceContainer.get_resource('cube-blur').encoding = sRGBEncoding;

    ResourceContainer.get_resource('alu-diffuse').repeat.set(6, 0.5);
    ResourceContainer.get_resource('alu-diffuse').wrapT = RepeatWrapping;
    ResourceContainer.get_resource('alu-diffuse').wrapS = RepeatWrapping;
    ResourceContainer.get_resource('alu-diffuse').encoding = sRGBEncoding;

    ResourceContainer.get_resource('noise').wrapT = MirroredRepeatWrapping;
    ResourceContainer.get_resource('noise').wrapS = MirroredRepeatWrapping;

    ResourceContainer.get_resource('alu-normal').repeat.set(6, 0.5);
    ResourceContainer.get_resource('alu-normal').wrapT = RepeatWrapping;
    ResourceContainer.get_resource('alu-normal').wrapS = RepeatWrapping;

    ResourceContainer.get_resource('alu-specular').repeat.set(6, 0.5);
    ResourceContainer.get_resource('alu-specular').wrapT = RepeatWrapping;
    ResourceContainer.get_resource('alu-specular').wrapS = RepeatWrapping;

    ResourceContainer.get_resource('styrofoam-diffuse3').wrapS = MirroredRepeatWrapping;
    ResourceContainer.get_resource('styrofoam-diffuse3').wrapS = MirroredRepeatWrapping;
    ResourceContainer.get_resource('styrofoam-diffuse3').encoding = sRGBEncoding;

    ResourceContainer.get_resource('sun').encoding = sRGBEncoding;

    SceneManager.backScene = new Scene();
    SceneManager.frontScene = new Scene();
    SceneManager.textScene = new Scene();

    this.sun = new Sun();
    this.sun.position.set( 1.0, 0.0, 1.5);
    this.sun.visible = false;
    SceneManager.current.add(this.sun);

    this.sky = new Sky();
    this.sky.scale.setScalar( 100 );
    SceneManager.current.add( this.sky );

    this.structure = new Structure();
    // Start with a default mono glass so the growth animation plays immediately
    this.structure.set({thickness: 1, layers:['10:0']},{thickness:0, layers:['0:0']},{thickness:0, layers:['0:0']},{thickness:0},{thickness:0});

    let floor_geometry = new PlaneGeometry( 1000, 1000 );
    floor_geometry.rotateX(Math.PI * -0.5);

    let floor_shadow_glass_material = new ShadowMaterial( { opacity: 0.025, dithering: true } );
    let floor_shadow_spacer_material = new ShadowMaterial( { opacity: 0.2, dithering: true } );
    
    this.floor_shadow_glass = new Mesh(floor_geometry, floor_shadow_glass_material);
    this.floor_spacer_glass = new Mesh(floor_geometry, floor_shadow_spacer_material);

    this.floor_shadow_glass.receiveShadow = true;
    this.floor_spacer_glass.receiveShadow = true;
    this.floor_shadow_glass.position.y = -0.0125;
    this.floor_spacer_glass.position.y = -0.0125;

    // Start with no shadows (Inside/Inside default)
    this.floor_shadow_glass.visible = false;
    this.floor_spacer_glass.visible = false;

    SceneManager.frontScene.add(this.floor_spacer_glass);
    SceneManager.current.add(this.floor_shadow_glass);

    SceneManager.frontScene.add(this.structure);


    this.config = ResourceContainer.get_resource('config');

    this.sections = Sections;

    this.home_view = new HomeView();
    this.home_view.start();

    this.scene_controller.start();

    DatGui.start();

    // Expose label visibility toggle for mobile bottom sheet
    window._cdrToggleLabels = (visible) => {
      SceneManager.textScene.visible = visible;
    };

    // Expose shadow floor toggle for application mode (inside/outside)
    window._cdrSetApplication = (mode) => {
      // mode: 'inside' | 'mixed' | 'outside'
      const showShadow = mode !== 'inside';
      this.floor_shadow_glass.visible = showShadow;
      this.floor_spacer_glass.visible = showShadow;

      if (mode === 'outside') {
        // Stronger shadows for full exterior
        this.floor_shadow_glass.material.opacity = 0.06;
        this.floor_spacer_glass.material.opacity = 0.35;
      } else {
        // Default shadow intensity
        this.floor_shadow_glass.material.opacity = 0.025;
        this.floor_spacer_glass.material.opacity = 0.2;
      }
    };

    this.go_to(Sections.INITIAL);
    document.addEventListener('contextmenu', (event) =>
    {
      event.preventDefault();
    }, false);

  }

  go_to(section)
  {
    ViewManager.go_to_view(section, false);
  }

  update()
  {

    this.scene_controller.update();
    this.structure.update();

    this.sky.update();
  }

  updateLabels(frg_w, og_w, mg_w, so_w, sm_w, u_value, g_o, g_m, rotate_frg) 
  {

    if(!this.structure) 
    {
      console.warn('.');
      
      setTimeout(
        (
          (()=>(
            this.updateLabels(frg_w, og_w, mg_w, so_w, sm_w, u_value, g_o, g_m)  
          )).bind(this)
        ),100
      );
      
      return;
    }

    this.structure.set(frg_w, mg_w, og_w, sm_w, so_w, rotate_frg);

  }

  //on_post_render() 
  //{
  //  Graphics.render(SceneManager.frontScene, CameraManager.current);
  //}

  setSun(aBoolean)
  {
    if (this.sun) this.sun.visible = false;
  }

  on_post_start()
  {
  }
}
